/**
 * Mitigates a third-party app (ProductWiz/Rio) fully unmounting and
 * remounting its gallery/options widgets on every variant or breakpoint
 * change, which briefly collapses them to 0 height and yanks the page
 * layout. This is a workaround for a bug in the vendor's own bundle, not a
 * fix to it — we do not own or modify ProductWiz's code. Instead we watch
 * two of its stable wrapper elements and reserve their last known-good
 * height (via an inline `min-height`, which always wins over the vendor's
 * own stylesheet regardless of load order) while the vendor's content is
 * torn down and rebuilt inside them, so the collapse is never visible.
 *
 * Selectors are pinned to the app's markup confirmed working as of
 * 2026-09-16 (Rio JS v4.159.0). If the vendor renames or restructures that
 * markup, this fails soft to a no-op — no reservation, no error — rather
 * than breaking the page.
 */

export const MIN_VALID_HEIGHT = 40;

export function pickReservedHeight(previousHeight, measuredHeight, minValidHeight = MIN_VALID_HEIGHT) {
  if (measuredHeight >= minValidHeight) {
    return Math.ceil(measuredHeight);
  }
  return previousHeight;
}

/* c8 ignore start */
(function () {
  // How often each anchor is re-measured. The tick is unconditional, so
  // this is also the worst-case latency for adopting a new height. Fast
  // enough that a real format swap settles within a frame or two of the
  // vendor's rebuild, slow enough that the forced layout read is noise.
  var POLL_INTERVAL_MS = 300;

  // Both anchors are normally present by DOMContentLoaded, but ProductWiz's
  // React bundle boots asynchronously and may mount either one later. Cap
  // how long we keep watching the document for a not-yet-found anchor.
  var MUTATION_FALLBACK_TIMEOUT_MS = 15000;

  var SELECTORS = ['.product__media-wrapper.rio-media-gallery', '.rio-options'];

  function toPx(value) {
    var n = parseFloat(value);
    return n === n ? n : 0; // NaN-safe: jsdom/older browsers may report ''
  }

  // getBoundingClientRect() reports the *border-box* height, but
  // `min-height` constrains the *content box* unless the element is
  // `box-sizing: border-box`. These anchors are vendor-injected and the
  // theme does not style them, so neither box-sizing can be assumed —
  // read it and, for content-box, strip the padding and borders so the
  // number we compare and the number we write are in the same units.
  function measureReservable(el) {
    var rectHeight = el.getBoundingClientRect().height;
    var cs = typeof window.getComputedStyle === 'function' ? window.getComputedStyle(el) : null;
    if (!cs || cs.boxSizing === 'border-box') return rectHeight;
    return (
      rectHeight -
      toPx(cs.paddingTop) -
      toPx(cs.paddingBottom) -
      toPx(cs.borderTopWidth) -
      toPx(cs.borderBottomWidth)
    );
  }

  // Reserves `el`'s last known-good height as an inline min-height while its
  // content is torn down/rebuilt.
  //
  // The decision is driven by an unconditional poll, deliberately *not* by a
  // ResizeObserver. An observer only fires when the observed box's rendered
  // size actually changes — and our own clamp is exactly what stops it from
  // changing. Once min-height is 685px (Hochformat), a swap to a naturally
  // shorter format (Querformat, ~320px) never moves the rendered height at
  // any observable instant: it is max(natural, 685) = 685 during teardown
  // and still 685 after the rebuild. No notification is ever delivered, so
  // an observer-driven design latches at the tallest height ever seen —
  // which is the very "unstable page" bug this file exists to prevent.
  //
  // The poll has no such blind spot because it never waits to be told
  // anything. On every tick it releases the clamp, synchronously measures
  // the real unclamped height, and either adopts it (real content) or keeps
  // the previous reservation (still mid-collapse, below MIN_VALID_HEIGHT).
  // Release, measure and reapply all happen inside one synchronous call —
  // getBoundingClientRect() forces layout but never a paint, so the
  // momentarily-unclamped state is never rendered.
  //
  // A tick that finds nothing changed simply rewrites the same value, which
  // is cheap and has no observable effect. The poll runs for the lifetime of
  // the page: the vendor can remount at any time the customer keeps
  // interacting, and a detached anchor just measures 0 and holds — so we
  // never need to guess when it is safe to stop.
  function attachShield(el) {
    var reservedHeight = 0;

    function tick() {
      el.style.minHeight = '';
      reservedHeight = pickReservedHeight(reservedHeight, measureReservable(el));
      el.style.minHeight = reservedHeight + 'px';
    }

    tick();
    var pollTimer = setInterval(tick, POLL_INTERVAL_MS);

    return {
      el: el,
      disconnect: function () {
        clearInterval(pollTimer);
      },
    };
  }

  // Shared attach logic for both the immediate (DOMContentLoaded) lookup and
  // the MutationObserver fallback below: attaches the shield to `selector`
  // if it isn't already attached, and re-arms the search if the
  // previously-found element has since been removed from the document
  // (i.e. the vendor replaced the anchor node itself, not just its
  // children).
  function makeWatcher(selector) {
    var active = null;

    function recheck() {
      if (active && !document.body.contains(active.el)) {
        active.disconnect();
        active = null;
      }
      if (active) return;
      var el = document.querySelector(selector);
      if (!el) return;
      active = attachShield(el);
    }

    recheck();

    return {
      recheck: recheck,
      isActive: function () {
        return !!active;
      },
    };
  }

  function init() {
    var watchers = SELECTORS.map(makeWatcher);

    function allFound() {
      return watchers.every(function (w) {
        return w.isActive();
      });
    }

    if (allFound() || typeof MutationObserver === 'undefined') return;

    var mutationObserver = new MutationObserver(function () {
      watchers.forEach(function (w) {
        w.recheck();
      });
      if (allFound()) {
        clearTimeout(capTimer);
        mutationObserver.disconnect();
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    var capTimer = setTimeout(function () {
      mutationObserver.disconnect();
    }, MUTATION_FALLBACK_TIMEOUT_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
/* c8 ignore stop */
